import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../core/auth/auth.service';
import { AuthStore } from '../../core/auth/auth.store';
import { ThemePreference, ThemeService } from '../../core/theme/theme.service';
import { AvatarComponent, IconComponent, MenuComponent, MenuEntry } from '../../ui';

/**
 * Area utente in fondo alla barra laterale: avatar, nome e menu con Profilo, Tema ed Esci
 * (FR-021). L'uscita chiude la sessione sul server e sostituisce la voce della cronologia, così
 * il tasto Indietro non mostra contenuti protetti (FR-011, SC-004).
 */
@Component({
  selector: 'bs-user-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AvatarComponent, IconComponent, MenuComponent],
  host: { class: 'block' },
  template: `
    <bs-menu
      appearance="block"
      placement="above"
      [label]="'Menu utente di ' + store.fullName()"
      testId="user-menu-button"
      [entries]="entries()"
      (selected)="onSelected($event)"
    >
      <bs-avatar
        [initials]="store.initials()"
        [userId]="store.userId()"
        [avatarVersion]="store.avatarVersion()"
      />
      <span class="flex min-w-0 flex-1 flex-col">
        <span
          class="truncate text-sm font-semibold text-ink dark:text-stone-50"
          [attr.title]="store.fullName()"
          data-testid="user-name"
          >{{ store.fullName() }}</span
        >
        <span
          class="truncate text-xs text-stone-600 dark:text-stone-300"
          [attr.title]="store.user()?.email"
          >{{ store.user()?.email }}</span
        >
      </span>
      <bs-icon name="more" [size]="18" />
    </bs-menu>
  `,
})
export class UserMenuComponent {
  protected readonly store = inject(AuthStore);
  private readonly auth = inject(AuthService);
  private readonly theme = inject(ThemeService);
  private readonly router = inject(Router);

  protected readonly entries = computed<MenuEntry[]>(() => {
    const preference = this.theme.preference();
    return [
      { kind: 'item', id: 'profile', label: 'Profilo', icon: 'user', testId: 'user-menu-profile' },
      { kind: 'separator' },
      { kind: 'heading', label: 'Tema' },
      {
        kind: 'radio',
        id: 'theme:light',
        label: 'Chiaro',
        icon: 'sun',
        checked: preference === 'light',
        testId: 'theme-light',
      },
      {
        kind: 'radio',
        id: 'theme:dark',
        label: 'Scuro',
        icon: 'moon',
        checked: preference === 'dark',
        testId: 'theme-dark',
      },
      {
        kind: 'radio',
        id: 'theme:system',
        label: 'Sistema',
        icon: 'monitor',
        checked: preference === 'system',
        testId: 'theme-system',
      },
      { kind: 'separator' },
      { kind: 'item', id: 'logout', label: 'Esci', icon: 'logout', testId: 'logout' },
    ];
  });

  protected onSelected(id: string): void {
    if (id === 'profile') {
      void this.router.navigate(['/profile']);
    } else if (id.startsWith('theme:')) {
      this.theme.set(id.slice('theme:'.length) as ThemePreference);
    } else if (id === 'logout') {
      this.auth.logout().subscribe(() => {
        void this.router.navigate(['/login'], { replaceUrl: true });
      });
    }
  }
}
